import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import { useState } from "react";

import { useUI } from "../../../../../../contexts/ui";
import { useAnalytics } from "../../../../../../utils/analytics";
import {
  useGenerateIntegrationSummaryMutation,
  useSubmitIntegrationSummaryMutation,
} from "../../../../../../utils/queries/projects";

type SummarizeIntegrationResponseDialogProps = {
  open: boolean;
  onOpenChange(open: boolean): void;
  ticketId: string;
};

const dataTestIdScope = "summarize-integration-response-dialog" as const;

export default function SummarizeIntegrationResponseDialog(
  props: SummarizeIntegrationResponseDialogProps,
) {
  const { open, onOpenChange, ticketId } = props;
  const { notify } = useUI();
  const analytics = useAnalytics("SummarizeIntegrationResponseDialog");

  // Mutations
  const generateSummaryMutation = useGenerateIntegrationSummaryMutation();
  const submitSummaryMutation = useSubmitIntegrationSummaryMutation();

  // State management
  const [selectedAction, setSelectedAction] = useState<"accept" | "reject">(
    "accept",
  );
  const [generatedSummary, setGeneratedSummary] = useState<string[]>([]);
  const [editedSummary, setEditedSummary] = useState<string[]>([]);
  const [showSummaryEditor, setShowSummaryEditor] = useState(false);

  // Reset state when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset all state when closing
      setSelectedAction("accept");
      setGeneratedSummary([]);
      setEditedSummary([]);
      setShowSummaryEditor(false);
    }
    onOpenChange(newOpen);
  };

  // Generate summary or submit directly for accept
  const handleGenerate = () => {
    if (selectedAction === "accept") {
      // For accept, submit directly without generating summary
      handleSubmitResponse();
    } else {
      // For reject, generate summary first
      generateSummaryMutation.mutate(
        { ticketId, action: selectedAction },
        {
          onSuccess(data: any) {
            if (data?.summary && Array.isArray(data.summary)) {
              setGeneratedSummary(data.summary);
              setEditedSummary([...data.summary]);
              setShowSummaryEditor(true);

              analytics.capture("integration_summary_generated", {
                ticketId,
                summaryLength: data.summary.length,
              });
            } else {
              throw new Error("No summary returned from server");
            }
          },
          onError: async (error) => {
            console.error("Failed to generate summary:", error);
            const message = await getErrorMessage(error);
            notify({
              title: "Failed to generate summary",
              message,
            });
            handleOpenChange(false);
          },
        },
      );
    }
  };

  // Helper functions for managing the array
  const updateEditedItem = (index: number, newValue: string) => {
    const updatedArray = [...editedSummary];
    updatedArray[index] = newValue;
    setEditedSummary(updatedArray);
  };

  const deleteEditedItem = (index: number) => {
    const updatedArray = editedSummary.filter((_, i) => i !== index);
    setEditedSummary(updatedArray);
  };

  // Check if arrays are different (deep comparison)
  const arraysAreEqual = (a: string[], b: string[]) => {
    return a.length === b.length && a.every((val, i) => val === b[i]);
  };

  // Helper function to extract error message
  const getErrorMessage = async (error: unknown): Promise<string> => {
    if (error instanceof Response) {
      try {
        const errorData = await error.json();
        if (
          errorData &&
          typeof errorData === "object" &&
          "message" in errorData
        ) {
          return typeof errorData.message === "string"
            ? errorData.message
            : "An unknown error occurred";
        }
      } catch {
        // If JSON parsing fails, fall through to default
      }
    }
    return "An unknown error occurred";
  };

  // Submit final response
  const handleSubmitResponse = () => {
    // Only send adjustedSummary if the user actually changed the array
    const adjustedSummary = !arraysAreEqual(editedSummary, generatedSummary)
      ? editedSummary
      : null;

    submitSummaryMutation.mutate(
      {
        ticketId,
        adjustedSummary,
        action: selectedAction,
      },
      {
        onSuccess() {
          analytics.capture("integration_summary_submitted", {
            ticketId,
            wasEdited: adjustedSummary !== null,
            finalLength: editedSummary.length,
            action: selectedAction,
          });

          notify({
            title: "Response submitted to PM tool",
            message:
              "Your response has been successfully sent to the project management tool.",
          });

          handleOpenChange(false);
        },
        onError: async (error) => {
          console.error("Failed to submit response:", error);
          const message = await getErrorMessage(error);
          notify({
            title: "Failed to submit response",
            message,
          });
          handleOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        style={{ maxWidth: "600px" }}
        data-testid={`${dataTestIdScope}-content`}
      >
        <Dialog.Title data-testid={`${dataTestIdScope}-title`}>
          Generate summary for PM tool
        </Dialog.Title>

        <Flex direction="column" gap="4" mt="4">
          {/* Action Selection Cards */}
          {!showSummaryEditor && (
            <>
              <p className="text-gray-500 mb-2 text-sm">
                Choose your response:
              </p>
              <div className="flex gap-4">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="action"
                    value="accept"
                    checked={selectedAction === "accept"}
                    onChange={() => setSelectedAction("accept")}
                    data-testid={`${dataTestIdScope}-accept-radio`}
                  />
                  <span className="font-medium">Accept</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="action"
                    value="reject"
                    checked={selectedAction === "reject"}
                    onChange={() => setSelectedAction("reject")}
                    data-testid={`${dataTestIdScope}-reject-radio`}
                  />
                  <span className="font-medium">Reject</span>
                </label>
              </div>

              {/* Generate/Submit Button */}
              <Button
                variant="outline"
                size="3"
                className="text-blue-600 border-blue-500 hover:bg-blue-50 mt-4 w-full bg-transparent"
                onClick={handleGenerate}
                disabled={
                  generateSummaryMutation.isPending ||
                  submitSummaryMutation.isPending
                }
                data-testid={`${dataTestIdScope}-generate-button`}
              >
                {generateSummaryMutation.isPending
                  ? "Generating..."
                  : submitSummaryMutation.isPending
                    ? "Submitting..."
                    : selectedAction === "accept"
                      ? "Submit"
                      : "Generate Summary"}
              </Button>
            </>
          )}

          {/* Summary Editor */}
          {showSummaryEditor && (
            <>
              <Text size="2" color="gray" mb="2">
                Review and edit the generated summary:
              </Text>

              {/* Individual Input Fields */}
              <div className="max-h-60 space-y-2 overflow-y-auto">
                {editedSummary.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <TextField.Root
                      value={item}
                      onChange={(e) => updateEditedItem(index, e.target.value)}
                      placeholder={`Action item ${index + 1}`}
                      className="flex-1"
                      data-testid={`${dataTestIdScope}-item-${index}`}
                    />
                    <button
                      type="button"
                      onClick={() => deleteEditedItem(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-2 py-1 text-sm"
                      data-testid={`${dataTestIdScope}-delete-${index}`}
                      title="Delete this item"
                    >
                      Delete
                    </button>
                  </div>
                ))}

                {editedSummary.length === 0 && (
                  <div className="text-gray-500 py-4 text-center">
                    No action items generated. Try clicking &quot;Generate Summary&quot;
                    again.
                  </div>
                )}
              </div>

              <Button
                size="3"
                className="mt-4 w-full"
                onClick={handleSubmitResponse}
                disabled={
                  submitSummaryMutation.isPending || editedSummary.length === 0
                }
                data-testid={`${dataTestIdScope}-submit-button`}
              >
                {submitSummaryMutation.isPending
                  ? "Submitting..."
                  : "Submit Response"}
              </Button>
            </>
          )}
        </Flex>

        <Flex gap="3" mt="5" justify="end">
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              data-testid={`${dataTestIdScope}-cancel-button`}
            >
              Cancel
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
